import { Button, Card, EmptyState, Skeleton } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CalendarClock, CheckCircle2, Coins, CreditCard, KeyRound, Minus, Package, Tags } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { ErreurChargement, Section } from '../../components/bits';
import { formatEuros, formatNombre, formatVariation } from '../../lib/format';
import { LIBELLE_TYPE, TYPES_COMPTE } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';

type Chiffres = components['schemas']['DashboardFigures'];
type Alerte = components['schemas']['Alert'];

const INDICATEURS: Array<{ cle: keyof Chiffres; libelle: string; format?: (n: number) => string }> = [
  { cle: 'activeStudents', libelle: 'Élèves actives' },
  { cle: 'signups', libelle: 'Inscriptions' },
  { cle: 'messagesSent', libelle: 'Messages envoyés' },
  { cle: 'imagesGenerated', libelle: 'Images générées' },
  { cle: 'videosGenerated', libelle: 'Vidéos générées' },
  { cle: 'creditsConsumed', libelle: 'Crédits consommés' },
  { cle: 'revenueCents', libelle: 'Montant encaissé (net)', format: formatEuros },
];

/** Où mène une alerte : une fiche, ou une liste filtrée. */
export function cheminAlerte(a: Alerte): string {
  const q = a.target.query && Object.keys(a.target.query).length ? `?${new URLSearchParams(a.target.query).toString()}` : '';
  switch (a.target.type) {
    case 'accessKey':
      return `/cles-acces/${a.target.id}`;
    case 'accessKeys':
      return `/cles-acces${q}`;
    case 'student':
      return `/eleves/${a.target.id}`;
    case 'webhooks':
      return `/ventes/paiements/webhooks${q}`;
    case 'payments':
      return `/ventes/paiements${q}`;
    default:
      return `/eleves${q}`;
  }
}

/**
 * Tableau de bord (cahier des charges §7.1) : chiffres clés sur 7, 30 ou
 * 90 jours avec la variation, ce qui demande une action, raccourcis.
 */
export function DashboardPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { valeurs, modifier } = useFiltresUrl({ periode: '30' });

  const tableau = useQuery({
    queryKey: ['dashboard', valeurs.periode],
    queryFn: () => lire(api.GET('/api/admin/dashboard', { params: { query: { days: Number(valeurs.periode) as 7 | 30 | 90 } } })),
    placeholderData: keepPreviousData,
  });
  const alertes = useQuery({ queryKey: ['alertes'], queryFn: () => lire(api.GET('/api/admin/alerts')) });

  const d = tableau.data;

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        lead={`Bonjour${user?.username ? ` ${user.username}` : ''}. Voici l’état de la plateforme.`}
        actions={
          <div className="segmented" role="group" aria-label="Période">
            {['7', '30', '90'].map((p) => (
              <button key={p} type="button" aria-pressed={valeurs.periode === p} onClick={() => modifier({ periode: p })}>
                {p} jours
              </button>
            ))}
          </div>
        }
      />

      {tableau.isError ? (
        <ErreurChargement erreur={tableau.error} onReessayer={() => void tableau.refetch()} />
      ) : (
        <div className="kpis" aria-busy={tableau.isFetching}>
          {INDICATEURS.map((i) => (
            <Indicateur key={i.cle} libelle={i.libelle} actuel={d?.current[i.cle]} precedent={d?.previous[i.cle]} format={i.format} jours={d?.days} />
          ))}
          <Card className="kpi">
            <div className="kpi__label">Crédits en circulation</div>
            <div className="kpi__value">{d ? formatNombre(d.creditsInCirculation) : <Skeleton width={80} height={28} />}</div>
            <div className="kpi__hint">Soldes cumulés, aujourd’hui</div>
          </Card>
        </div>
      )}

      <div className="dash-grid">
        <div>
          <Section title="À traiter">
            {alertes.isError ? (
              <ErreurChargement erreur={alertes.error} onReessayer={() => void alertes.refetch()} />
            ) : !alertes.data ? (
              <div className="stack">
                <Skeleton height={48} />
                <Skeleton height={48} />
              </div>
            ) : alertes.data.alerts.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<CheckCircle2 size={26} aria-hidden="true" />}
                  title="Rien à traiter"
                  text="Aucun paiement Stripe en échec, aucune clé pleine ou sur le point d’expirer, aucune vidéo en échec sur 24 h."
                />
              </Card>
            ) : (
              <ul className="alerts">
                {alertes.data.alerts.map((a) => (
                  <li key={a.id}>
                    <button type="button" className={`alert-item alert-item--${a.severity}`} onClick={() => navigate(cheminAlerte(a))}>
                      <AlertTriangle size={16} className="alert-item__icon" aria-hidden="true" />
                      <span>
                        <span className="alert-item__title">{a.title}</span>
                        <span className="alert-item__detail">{a.detail}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Activité quotidienne">
            <Card>
              {d ? <Histogramme series={d.series} /> : <Skeleton height={160} />}
              <div className="legend">
                <span>Messages envoyés par jour (heure de Paris)</span>
              </div>
            </Card>
          </Section>
        </div>

        <div>
          <Section title="Raccourcis">
            <div className="shortcuts">
              <Button icon={<KeyRound size={15} aria-hidden="true" />} onClick={() => navigate('/cles-acces?creer=1')}>
                Créer des clés d’accès
              </Button>
              <Button icon={<Coins size={15} aria-hidden="true" />} onClick={() => navigate('/credits/ajout-groupe')}>
                Ajouter des crédits
              </Button>
              {user?.isSuperAdmin ? (
                <Button icon={<Package size={15} aria-hidden="true" />} onClick={() => navigate('/ventes/packs?creer=1')}>
                  Créer un pack
                </Button>
              ) : (
                <Button icon={<CreditCard size={15} aria-hidden="true" />} onClick={() => navigate('/ventes/paiements')}>
                  Voir les paiements
                </Button>
              )}
              <Button icon={<CalendarClock size={15} aria-hidden="true" />} onClick={() => navigate('/evenements')}>
                Régler un événement
              </Button>
              <Button icon={<Tags size={15} aria-hidden="true" />} onClick={() => navigate('/credits/bareme')}>
                Prix des générations
              </Button>
            </div>
          </Section>

          <Section title="Comptes élèves">
            <Card>
              {d ? (
                <>
                  <div className="big-number">{formatNombre(d.students.total)}</div>
                  <ul className="plain-list" style={{ marginTop: 8 }}>
                    {TYPES_COMPTE.map((t) => (
                      <li key={t} className="row row--between">
                        <button type="button" className="link-btn" onClick={() => navigate(`/eleves?type=${t}`)}>
                          {LIBELLE_TYPE[t]}
                        </button>
                        <span className="num">{formatNombre(d.students.byType[t] ?? 0)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="note">Hors admins et comptes supprimés.</p>
                </>
              ) : (
                <Skeleton height={120} />
              )}
            </Card>
          </Section>
        </div>
      </div>
    </>
  );
}

function Indicateur({
  libelle,
  actuel,
  precedent,
  format = formatNombre,
  jours,
}: {
  libelle: string;
  actuel?: number;
  precedent?: number;
  format?: (n: number) => string;
  jours?: number;
}) {
  const variation = actuel !== undefined && precedent !== undefined ? formatVariation(actuel, precedent) : null;
  const Icone = variation?.sens === 'hausse' ? ArrowUpRight : variation?.sens === 'baisse' ? ArrowDownRight : Minus;
  return (
    <Card className="kpi">
      <div className="kpi__label">{libelle}</div>
      <div className="kpi__value">{actuel === undefined ? <Skeleton width={80} height={28} /> : format(actuel)}</div>
      {actuel !== undefined && precedent !== undefined && (
        <>
          <div className={`kpi__delta kpi__delta--${variation?.sens ?? 'stable'}`}>
            <Icone size={13} aria-hidden="true" />
            {!variation ? 'Nouveau' : variation.sens === 'stable' ? 'Stable' : variation.texte}
          </div>
          <div className="kpi__hint">
            {jours} jours précédents : {format(precedent)}
          </div>
        </>
      )}
    </Card>
  );
}

/** Histogramme SVG des messages par jour, sans bibliothèque. */
export function Histogramme({ series }: { series: Array<{ date: string; messagesSent: number; signups: number }> }) {
  const largeur = 900;
  const hauteur = 150;
  const max = Math.max(1, ...series.map((s) => s.messagesSent));
  const pas = largeur / Math.max(1, series.length);
  const barre = Math.max(2, pas * 0.7);
  const etiquettes = series.length <= 10 ? 1 : series.length <= 31 ? 7 : 15;
  return (
    <svg className="chart" viewBox={`0 0 ${largeur} ${hauteur + 18}`} role="img" aria-label={`Messages envoyés par jour, maximum ${formatNombre(max)}`}>
      <line className="chart__axis" x1={0} x2={largeur} y1={hauteur} y2={hauteur} />
      {series.map((s, i) => {
        const h = (s.messagesSent / max) * (hauteur - 8);
        return (
          <g key={s.date}>
            <rect className="chart__bar" x={i * pas + (pas - barre) / 2} y={hauteur - h} width={barre} height={h} rx={2}>
              <title>
                {s.date.split('-').reverse().join('/')} : {formatNombre(s.messagesSent)} messages, {formatNombre(s.signups)} inscriptions
              </title>
            </rect>
            {i % etiquettes === 0 && (
              <text className="chart__label" x={i * pas + pas / 2} y={hauteur + 13} textAnchor="middle">
                {s.date.slice(8, 10)}/{s.date.slice(5, 7)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
